import { createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';
import type {
  ChatRoom,
  ChatState,
  SetHistoryPayload,
  AddChatMessagePayload,
  UpdateLastReadMessagesPayload,
  SetIsTypingPayload,
  SetPendingMessagePayload,
} from './chat.types';
import {
  otherParticipantLeft,
  realtimeInstance,
} from '../realtime/realtime.slice';

/* istanbul ignore next */
const getInitialState = (): ChatState => ({ rooms: {} });

export const getRoom = (state: ChatState, otherUserId: string): ChatRoom => {
  if (state.rooms[otherUserId] === undefined) {
    state.rooms[otherUserId] = {
      history: [],
      lastReadTimestamp: 0,
      isTyping: false,
      pendingMessage: '',
    };
  }

  return state.rooms[otherUserId] as ChatRoom;
};

/* istanbul ignore next */
export const _setHistory = (
  state: Draft<ChatState>,
  action: PayloadAction<SetHistoryPayload>,
): void => {
  const { otherUserId, message } = action.payload;
  getRoom(state, otherUserId).history = message;
};

/**
 * Add a chat message to a given user's history
 * @param {Draft<ChatState>} state chat state
 * @param {PayloadAction<AddChatMessagePayload>} action addChatMessage action payload
 *
 */
export const _addChatMessage = (
  state: Draft<ChatState>,
  action: PayloadAction<AddChatMessagePayload>,
): void => {
  const { otherUserId, message } = action.payload;
  getRoom(state, otherUserId).history.push(message);
};

/**
 * Update the last read timestamp for a given user
 * @param {Draft<ChatState>} state chat state
 * @param {PayloadAction<UpdateLastReadMessagesPayload>} action updateReadMessages action payload
 *
 */
export const _updateReadMessages = (
  state: Draft<ChatState>,
  action: PayloadAction<UpdateLastReadMessagesPayload>,
): void => {
  const { otherUserId } = action.payload;
  const history = getRoom(state, otherUserId).history;
  const lastMessageTimestamp = history[history.length - 1]?.timestamp;
  getRoom(state, otherUserId).lastReadTimestamp =
    lastMessageTimestamp ?? Date.now();
};

/**
 * Set the isTyping status for a given user
 * @param {Draft<ChatState>} state chat state
 * @param {PayloadAction<unknown>} action setIsTyping action payload
 */

export const _setIsTyping = (
  state: Draft<ChatState>,
  action: PayloadAction<SetIsTypingPayload>,
): void => {
  const { otherUserId, isTyping } = action.payload;
  getRoom(state, otherUserId).isTyping = isTyping;
};

export const _setPendingMessage = (
  state: Draft<ChatState>,
  action: PayloadAction<SetPendingMessagePayload>,
): void => {
  const { pendingMessage, userId } = action.payload;
  getRoom(state, userId).pendingMessage = pendingMessage;
};

/* istanbul ignore next */
export const chatSlice = createSlice({
  name: 'chat',
  initialState: () => getInitialState(),
  reducers: {
    setHistory: _setHistory,
    addChatMessage: _addChatMessage,
    setIsTyping: _setIsTyping,
    updateReadMessages: _updateReadMessages,
    setPendingMessage: _setPendingMessage,
  },
  extraReducers: (builder) => {
    builder.addCase(otherParticipantLeft, (state) => {
      // Mark chats as read when participants are no longer in the waiting room
      // This prevents unread message badges from persisting after a participant leaves
      for (const userId in state.rooms) {
        // Mark room as read if the user is no longer in the members map
        if (!(userId in realtimeInstance.membersMap)) {
          const room = state.rooms[userId];

          if (room === undefined) {
            continue;
          }

          // Get the last message timestamp (same as in `_updateReadMessages`)
          const lastMessageTimestamp =
            room.history[room.history.length - 1]?.timestamp;

          room.lastReadTimestamp = lastMessageTimestamp ?? Date.now();
          room.isTyping = false;
          room.pendingMessage = '';
        }
      }
    });
  },
});

export const {
  setHistory,
  addChatMessage,
  updateReadMessages,
  setIsTyping,
  setPendingMessage,
} = chatSlice.actions;
